import api from "../../../utils/api";
import {
  WORK_HOURS_ENDPOINTS,
  type ActivityCost,
  type ActivityStatus,
  type ActivityType,
  type ActivityTypeInput,
  type SprintStatus,
  type WorkActivity,
  type WorkSprint,
} from "@torre/work-hours";

// ---------------------------------------------------------------------------
// Cliente API de Gestión de Staff › Actividades (épica #1299). Habla con las
// rutas `workHoursActivities*` / `workHoursSprints*` / `workHoursActivityCatalog`
// del módulo de Horas (contrato en docs/actividades/contrato-api.md).
//
// Estas rutas responden con CUERPO PLANO (sin el sobre `{ meta, data }` del
// `response()` clásico): listas `{ data, meta }`, altas `{ ok, created, data }`,
// errores `{ error: { code, message } }`. Por eso aquí se lee `res.data` directo.
// ---------------------------------------------------------------------------

const body = <T>(res: { data?: unknown }): T => res?.data as T;

/** Mensaje del contrato de error (`{ error: { code, message } }`), o `null`. */
export function activityErrorMessage(err: unknown): string | null {
  const e = err as { response?: { data?: { error?: { message?: string }; meta?: { message?: string } } } };
  return e?.response?.data?.error?.message ?? e?.response?.data?.meta?.message ?? null;
}

export function activityErrorCode(err: unknown): string | null {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
  return e?.response?.data?.error?.code ?? (e?.response?.status ? String(e.response.status) : null);
}

// --- Catálogo (#1300 / #1305) ------------------------------------------------

export interface CatalogResponse {
  data: ActivityType[];
  meta: {
    canManage: boolean;
    role: string | null;
    /** Claves que mi puesto puede realizar. */
    allowedKeys: string[];
    /** Claves que mi puesto puede registrar a mano («Registrar tiempo»). */
    manualKeys: string[];
    materialized?: boolean;
  };
}

export async function fetchActivityCatalog(): Promise<CatalogResponse> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.activityCatalog);
  const b = body<Partial<CatalogResponse>>(res) ?? {};
  return {
    data: Array.isArray(b.data) ? b.data : [],
    meta: {
      canManage: Boolean(b.meta?.canManage),
      role: b.meta?.role ?? null,
      allowedKeys: b.meta?.allowedKeys ?? [],
      manualKeys: b.meta?.manualKeys ?? [],
      materialized: b.meta?.materialized,
    },
  };
}

export async function saveActivityType(input: Partial<ActivityTypeInput> & { key: string }): Promise<ActivityType> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.activityCatalog, input);
  return body<{ ok: boolean; data: ActivityType }>(res).data;
}

export async function deleteActivityType(key: string): Promise<void> {
  await api.delete(`${WORK_HOURS_ENDPOINTS.activityCatalog}/${encodeURIComponent(key)}`);
}

// --- Tickets (#1301 / #1304) --------------------------------------------------

/** Ticket como lo devuelve el API: `cost` y `assigneeRole` vienen del servidor (costo SOLO a dueños). */
export interface ActivityRow extends WorkActivity {
  cost?: ActivityCost | null;
  assigneeRole?: string | null;
  /** Etiquetas propias del ticket (#1595); las de GitHub viven en `github.labels`. */
  labels?: string[];
}

export interface ActivitiesMeta {
  total: number;
  hasMore: boolean;
  canManage: boolean;
  isOwner: boolean;
  activeSprintId: string | null;
}

export interface ActivitiesResponse {
  data: ActivityRow[];
  meta: ActivitiesMeta;
}

export interface ActivitiesQuery {
  status?: ActivityStatus;
  sprintId?: string;
  backlog?: boolean;
  assigneeTeamMemberId?: string;
  parentId?: string;
  source?: string;
  q?: string;
  includeDone?: boolean;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
}

export async function fetchActivities(query: ActivitiesQuery = {}): Promise<ActivitiesResponse> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    params[k] = v === true ? 1 : (v as string | number);
  }
  const res = await api.get(WORK_HOURS_ENDPOINTS.activities, { params });
  const b = body<Partial<ActivitiesResponse>>(res) ?? {};
  return {
    data: Array.isArray(b.data) ? b.data : [],
    meta: {
      total: Number(b.meta?.total ?? (Array.isArray(b.data) ? b.data.length : 0)),
      hasMore: Boolean(b.meta?.hasMore),
      canManage: Boolean(b.meta?.canManage),
      isOwner: Boolean(b.meta?.isOwner),
      activeSprintId: b.meta?.activeSprintId ?? null,
    },
  };
}

export async function fetchActivity(id: string): Promise<ActivityRow | null> {
  // El contrato no tiene GET por id: se pide la lista incluyendo hechos y se
  // busca la fila. Un ticket concreto (deep link `?ticket=`) rara vez cae fuera.
  const res = await fetchActivities({ includeDone: true, limit: 2000 });
  return res.data.find((a) => a.id === id) ?? null;
}

/** Cuerpo de alta/edición. Todo opcional salvo título y tipo al crear. */
export interface ActivityPayload {
  title?: string;
  description?: string | null;
  typeKey?: string;
  status?: ActivityStatus | null;
  assigneeTeamMemberId?: string | null;
  sprintId?: string | null;
  storyPoints?: number | null;
  estimateMinutes?: number | null;
  startDate?: string | null;
  dueDate?: string | null;
  priority?: WorkActivity["priority"] | null;
  parentId?: string | null;
  projectId?: string | null;
  repo?: string | null;
  key?: string | null;
  githubRef?: string | null;
  /** Épica propia (milestone del equipo). */
  isEpic?: boolean;
}

export async function createActivity(payload: ActivityPayload): Promise<{ created: boolean; data: ActivityRow }> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.activities, payload);
  const b = body<{ ok: boolean; created: boolean; data: ActivityRow }>(res);
  return { created: Boolean(b?.created), data: b?.data };
}

export async function updateActivity(id: string, payload: ActivityPayload): Promise<ActivityRow> {
  const res = await api.patch(`${WORK_HOURS_ENDPOINTS.activity}/${encodeURIComponent(id)}`, payload);
  const b = body<{ ok: boolean; data: ActivityRow }>(res);
  return b?.data;
}

export async function updateActivityStatus(id: string, status: ActivityStatus): Promise<ActivityRow> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.activityStatus, { id, status });
  const b = body<{ ok: boolean; data: ActivityRow }>(res);
  return b?.data;
}

/** Archivar (dueños): nada se borra. */
export async function archiveActivity(id: string): Promise<void> {
  await api.delete(`${WORK_HOURS_ENDPOINTS.activity}/${encodeURIComponent(id)}`);
}

// --- Sprints ------------------------------------------------------------------

export interface SprintsResponse {
  data: WorkSprint[];
  meta: { activeSprintId: string | null };
}

export async function fetchSprints(): Promise<SprintsResponse> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.sprints);
  const b = body<Partial<SprintsResponse>>(res) ?? {};
  const data = Array.isArray(b.data) ? b.data : [];
  return { data, meta: { activeSprintId: b.meta?.activeSprintId ?? data.find((s) => s.status === "active")?.id ?? null } };
}

export interface SprintPayload {
  name?: string;
  goal?: string | null;
  startDate?: string;
  endDate?: string;
  status?: SprintStatus;
}

export async function createSprint(payload: SprintPayload): Promise<WorkSprint> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.sprints, payload);
  return body<{ ok: boolean; data: WorkSprint }>(res).data;
}

export async function updateSprint(id: string, payload: SprintPayload): Promise<{ data: WorkSprint; movedToBacklog?: number }> {
  const res = await api.patch(`${WORK_HOURS_ENDPOINTS.sprint}/${encodeURIComponent(id)}`, payload);
  const b = body<{ ok: boolean; data: WorkSprint; movedToBacklog?: number }>(res);
  return { data: b?.data, movedToBacklog: b?.movedToBacklog };
}

// --- Proyectos de Horas (para los filtros de alcance) --------------------------

/** Proyecto del catálogo de Horas: asignaciones (`allocation`), clientes del CRM (`client`) o `crm`. */
export interface WorkProjectOption {
  id: string;
  label: string;
  source: string;
  repos: string[];
  knownRepos?: string[];
}

/** `GET /workHoursProjects?scope=all`: todos los proyectos de la empresa con sus repos. */
export async function fetchWorkProjects(): Promise<WorkProjectOption[]> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.projects, { params: { scope: "all" } });
  const b = body<{ data?: WorkProjectOption[] }>(res) ?? {};
  return Array.isArray(b.data) ? b.data : [];
}

// --- GitHub ------------------------------------------------------------------

export interface SyncStatus {
  configured: boolean;
  /** ¿Quien consulta puede disparar la sincronización? (dueños) */
  canManage?: boolean;
  /** Regla de repos por omisión de la plataforma: org(s) + palabras del nombre ([] = todos). */
  defaultRepoRule?: { orgs: string[]; keywords: string[] } | null;
  sync?: { lastRunAt?: string | null; status?: string | null; repos?: string[]; created?: number; updated?: number; reason?: string | null } | null;
  lastRunAt?: string | null;
  status?: string | null;
  repos?: string[];
  created?: number;
  updated?: number;
  error?: string | null;
}

export async function fetchSyncStatus(): Promise<SyncStatus> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.activitiesSync);
  const b = body<Partial<SyncStatus>>(res) ?? {};
  return { configured: b.configured !== false, ...b };
}

export async function runGithubSync(): Promise<SyncStatus> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.activitiesSync, {});
  const b = body<Partial<SyncStatus>>(res) ?? {};
  return { configured: b.configured !== false, ...b };
}

// --- Insights y roadmap --------------------------------------------------------

export interface InsightsResponse {
  byStatus: Partial<Record<ActivityStatus, number>>;
  byType: { typeKey: string; count: number; minutes: number }[];
  byAssignee: {
    teamMemberId: string | null;
    name: string;
    open: number;
    done: number;
    points: number;
    loggedMinutes: number;
    cost?: { actual: number; estimated: number | null; currency: string } | null;
  }[];
  stuck: { id: string; key: string; title: string; status: ActivityStatus; assigneeName: string | null; days: number; since: string }[];
  overdue: { id: string; key: string; title: string; status: ActivityStatus; assigneeName: string | null; dueDate: string }[];
  sprint: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    points: { total: number; done: number };
    estimateMinutes: number;
    loggedMinutes: number;
    cost?: { estimated: number; actual: number; byCurrency?: Record<string, { estimated: number; actual: number }> } | null;
  } | null;
  velocity: { sprintId: string; name: string; pointsDone: number }[];
}

export async function fetchInsights(params: { sprintId?: string; all?: boolean } = {}): Promise<InsightsResponse> {
  const query: Record<string, string | number> = {};
  if (params.sprintId) query.sprintId = params.sprintId;
  if (params.all) query.all = 1;
  const res = await api.get(WORK_HOURS_ENDPOINTS.activitiesInsights, { params: query });
  const b = body<Partial<InsightsResponse>>(res) ?? {};
  return {
    byStatus: b.byStatus ?? {},
    byType: b.byType ?? [],
    byAssignee: b.byAssignee ?? [],
    stuck: b.stuck ?? [],
    overdue: b.overdue ?? [],
    sprint: b.sprint ?? null,
    velocity: b.velocity ?? [],
  };
}

export interface RoadmapRow {
  epic: ActivityRow;
  children: { total: number; done: number; inProgress: number };
  points: { total: number; done: number };
  startDate: string | null;
  dueDate: string | null;
  loggedMinutes: number;
  progress: number;
  cost?: ActivityCost | null;
}

export async function fetchRoadmap(): Promise<RoadmapRow[]> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.activitiesRoadmap);
  const b = body<{ data?: RoadmapRow[] }>(res) ?? {};
  return Array.isArray(b.data) ? b.data : [];
}
